import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./features/auth/useAuth";
import { clearReturnTo, markSignedIn, peekReturnTo, rememberReturnTo } from "./lib/sessionRedirect";
import Dashboard from "./pages/Dashboard";
import JobLogsPage from "./pages/JobLogsPage";
import Settings from "./pages/Settings";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import Approvals from "./pages/Approvals";
import ResetPassword from "./pages/ResetPassword";
import LocationAdmins from "./pages/LocationAdmins";
import LocationUsers from "./pages/LocationUsers";
import DirectoryHealth from "./pages/DirectoryHealth";
import ActivationOverview from "./pages/ActivationOverview";

/** Signed out on a protected page (expired session, or an emailed link): remember it, then sign in. */
const SignInRedirect = () => {
  const location = useLocation();
  rememberReturnTo(`${location.pathname}${location.search}`);
  return <Navigate to="/login" replace />;
};

/** Just signed in: go back to the remembered page, or the dashboard. */
const PostLoginRedirect = ({ forcePasswordReset }: { forcePasswordReset: boolean }) => {
  const target = forcePasswordReset ? "/reset-password" : (peekReturnTo() ?? "/");
  useEffect(() => {
    markSignedIn();
    if (!forcePasswordReset) {
      clearReturnTo();
    }
  }, [forcePasswordReset]);
  return <Navigate to={target} replace />;
};

const App = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1rem",
          color: "#64748b"
        }}
      >
        Loading session...
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/reset-password"
        element={user ? <ResetPassword /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/login"
        element={user ? <PostLoginRedirect forcePasswordReset={Boolean(user.forcePasswordReset)} /> : <Login />}
      />
      <Route
        path="/"
        element={
          user ? (
            user.forcePasswordReset ? (
              <Navigate to="/reset-password" replace />
            ) : (
            <Layout>
              <Dashboard />
            </Layout>
            )
          ) : (
            <SignInRedirect />
          )
        }
      />
      <Route
        path="/jobs/:jobId/logs"
        element={
          user ? (
            user.forcePasswordReset ? (
              <Navigate to="/reset-password" replace />
            ) : (
            <Layout>
              <JobLogsPage />
            </Layout>
            )
          ) : (
            <SignInRedirect />
          )
        }
      />
      <Route
        path="/settings"
        element={
          user ? (
            user.forcePasswordReset ? (
              <Navigate to="/reset-password" replace />
            ) : (
            <Layout>
              <Settings />
            </Layout>
            )
          ) : (
            <SignInRedirect />
          )
        }
      />
      <Route
        path="/approvals"
        element={
          user ? (
            user.forcePasswordReset ? (
              <Navigate to="/reset-password" replace />
            ) : (
            <Layout>
              <Approvals />
            </Layout>
            )
          ) : (
            <SignInRedirect />
          )
        }
      />
      <Route
        path="/location-admins"
        element={
          user ? (
            user.forcePasswordReset ? (
              <Navigate to="/reset-password" replace />
            ) : (
            <Layout>
              <LocationAdmins />
            </Layout>
            )
          ) : (
            <SignInRedirect />
          )
        }
      />
      <Route
        path="/location-users"
        element={
          user ? (
            user.forcePasswordReset ? (
              <Navigate to="/reset-password" replace />
            ) : (
            <Layout>
              <LocationUsers />
            </Layout>
            )
          ) : (
            <SignInRedirect />
          )
        }
      />
      <Route
        path="/directory-health"
        element={
          user ? (
            user.forcePasswordReset ? (
              <Navigate to="/reset-password" replace />
            ) : (
            <Layout>
              <DirectoryHealth />
            </Layout>
            )
          ) : (
            <SignInRedirect />
          )
        }
      />
      <Route
        path="/activation"
        element={
          user ? (
            user.forcePasswordReset ? (
              <Navigate to="/reset-password" replace />
            ) : (
            <Layout>
              <ActivationOverview />
            </Layout>
            )
          ) : (
            <SignInRedirect />
          )
        }
      />
      <Route
        path="*"
        element={<Navigate to={user ? (user.forcePasswordReset ? "/reset-password" : "/") : "/login"} replace />}
      />
    </Routes>
  );
};

export default App;
